import { kleur } from '../theme/tokens';
import { gedeeld } from '../theme/stijlen';
import React, { useState, useEffect } from 'react';
import { MessageSquare, Users, ClipboardList, BarChart3 } from 'lucide-react';
import { maakApi } from '../api/client';
import { Portret } from '../components/Portret';

function AdminDashboard({ user, token, onLogout, apiUrl }) {
  const { verzoek } = maakApi({ apiUrl, token, bijVerlopenToken: onLogout });

  const [activeTab, setActiveTab] = useState('klassen');
  // Welk groepje zijn gesprek open heeft staan in het resultatenoverzicht.
  const [openGesprek, setOpenGesprek] = useState(null);
  const [klassen, setKlassen] = useState([]);
  const [stellingen, setStellingen] = useState([]);
  const [personas, setPersonas] = useState([]);
  const [sessies, setSessies] = useState([]);
  const [resultaten, setResultaten] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [showStudentForm, setShowStudentForm] = useState(false);
  const [showSessieForm, setShowSessieForm] = useState(false);
  const [newKlasNaam, setNewKlasNaam] = useState('');
  const [newKlasLeerjaar, setNewKlasLeerjaar] = useState(6);
  const [selectedKlas, setSelectedKlas] = useState(null);
  const [selectedStelling, setSelectedStelling] = useState(null);
  const [selectedPersona, setSelectedPersona] = useState(null);
  const [selectedSessieId, setSelectedSessieId] = useState(null);
  const [newStudent, setNewStudent] = useState({ naam: '', username: '', password: '' });
  const [newSessie, setNewSessie] = useState({ statement_id: '', class_id: '', datum_vanaf: '' });
  const [agentConfig, setAgentConfig] = useState({
    6: {agents: [], kennis_verdeling: 'pro_plus', tijdsduur_chat: 15, tijdsduur_evaluatie: 5}, 
    7: {agents: [], kennis_verdeling: 'pro_plus', tijdsduur_chat: 15, tijdsduur_evaluatie: 5}, 
    8: {agents: [], kennis_verdeling: 'pro_plus', tijdsduur_chat: 15, tijdsduur_evaluatie: 5}
  });
  const [selectedLeerjaar, setSelectedLeerjaar] = useState(6);
  const [stellingPersonas, setStellingPersonas] = useState({6: [], 7: [], 8: []});
  const [allPersonas, setAllPersonas] = useState([]);
  const [rollenVerdeling, setRollenVerdeling] = useState({
    6: { expert_pro: 0, expert_contra: 0, kennis_pro: 0, kennis_contra: 0 },
    7: { expert_pro: 0, expert_contra: 0, kennis_pro: 0, kennis_contra: 0 },
    8: { expert_pro: 0, expert_contra: 0, kennis_pro: 0, kennis_contra: 0 }
  });

  useEffect(() => {
    if (activeTab === 'klassen') fetchKlassen();
    else if (activeTab === 'stellingen') fetchStellingen();
    else if (activeTab === 'personas') fetchPersonas();
    else if (activeTab === 'sessies') { fetchSessies(); fetchKlassen(); fetchStellingen(); }
    else if (activeTab === 'resultaten') { fetchSessies(); fetchKlassen(); fetchStellingen(); }
    // Alleen bij een tabwissel; de fetch-functies hierboven veranderen bij
    // elke render van identiteit en horen daarom niet in de lijst.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const fetchKlassen = async () => {
    setLoading(true);
    try {
      const response = await verzoek('/admin/classes');
      const data = await response.json();
      if (data.success) setKlassen(data.data);
    } catch (err) { console.error('Fout:', err); }
    finally { setLoading(false); }
  };

  const createKlas = async (e) => {
    e.preventDefault();
    try {
      const response = await verzoek('/admin/classes?naam=' + encodeURIComponent(newKlasNaam) + '&leerjaar=' + newKlasLeerjaar + '&teacher_id=' + user.id + '&school_id=' + user.school_id, { method: 'POST' });
      const data = await response.json();
      if (data.success) { setNewKlasNaam(''); setNewKlasLeerjaar(6); setShowForm(false); fetchKlassen(); }
    } catch (err) { console.error('Fout:', err); }
  };

  const deleteKlas = async (klasId) => {
    if (!window.confirm('Weet je zeker dat je deze klas wilt verwijderen?')) return;
    try {
      const response = await verzoek('/admin/classes/' + klasId, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) { fetchKlassen(); setSelectedKlas(null); }
    } catch (err) { console.error('Fout:', err); }
  };

  const viewKlas = async (klasId) => {
    try {
      const response = await verzoek('/admin/classes/' + klasId);
      const data = await response.json();
      if (data.success) setSelectedKlas(data.data);
    } catch (err) { console.error('Fout:', err); }
  };

  const addStudent = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams({ naam: newStudent.naam, username: newStudent.username, password: newStudent.password, class_id: selectedKlas.id, school_id: user.school_id });
      const response = await verzoek('/admin/students?' + params, { method: 'POST' });
      const data = await response.json();
      if (data.success) { setNewStudent({ naam: '', username: '', password: '' }); setShowStudentForm(false); viewKlas(selectedKlas.id); }
      else { alert(data.detail || 'Fout'); }
    } catch (err) { console.error('Fout:', err); }
  };

  const deleteStudent = async (studentId) => {
    if (!window.confirm('Weet je zeker?')) return;
    try {
      const response = await verzoek('/admin/students/' + studentId, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) viewKlas(selectedKlas.id);
    } catch (err) { console.error('Fout:', err); }
  };

  const fetchStellingen = async () => {
    setLoading(true);
    try {
      const response = await verzoek('/admin/statements');
      const data = await response.json();
      if (data.success) setStellingen(data.data);
    } catch (err) { console.error('Fout:', err); }
    finally { setLoading(false); }
  };

  const viewStelling = async (stellingId) => {
    try {
      const response = await verzoek('/admin/statements/' + stellingId);
      const data = await response.json();
      if (data.success) {
        setSelectedStelling(data.data);
        fetchAgentConfig(stellingId);
        fetchStellingPersonas(stellingId);
        fetchAllPersonas();
      }
    } catch (err) { console.error('Fout:', err); }
  };

  const fetchAgentConfig = async (stellingId) => {
    try {
      const response = await verzoek('/admin/statements/' + stellingId + '/agent-config');
      const data = await response.json();
      if (data.success) {
        setAgentConfig(data.data);

        // Converteer agents array naar rollenVerdeling formaat
        const newRollenVerdeling = {
          6: { expert_pro: 0, expert_contra: 0, kennis_pro: 0, kennis_contra: 0 },
          7: { expert_pro: 0, expert_contra: 0, kennis_pro: 0, kennis_contra: 0 },
          8: { expert_pro: 0, expert_contra: 0, kennis_pro: 0, kennis_contra: 0 }
        };

        [6, 7, 8].forEach(leerjaar => {
          const agents = data.data[leerjaar]?.agents || [];
          agents.forEach(agent => {
            if (agent.agent_rol && newRollenVerdeling[leerjaar].hasOwnProperty(agent.agent_rol)) {
              newRollenVerdeling[leerjaar][agent.agent_rol] = agent.aantal || 0;
            }
          });
        });

        setRollenVerdeling(newRollenVerdeling);
      }
    } catch (err) { console.error('Fout:', err); }
  };

  const fetchStellingPersonas = async (stellingId) => {
    try {
      const response = await verzoek('/admin/statements/' + stellingId + '/personas');
      const data = await response.json();
      if (data.success) {
        // Backend retourneert nu {6: [], 7: [], 8: []}
        if (data.data && typeof data.data === 'object' && !Array.isArray(data.data)) {
          setStellingPersonas(data.data);
        } else {
          // Fallback voor oude data (array formaat)
          setStellingPersonas({6: data.data || [], 7: [], 8: []});
        }
      }
    } catch (err) { console.error('Fout:', err); }
  };

  const fetchAllPersonas = async () => {
    try {
      const response = await verzoek('/admin/personas');
      const data = await response.json();
      if (data.success) setAllPersonas(data.data.standaard || []);
    } catch (err) { console.error('Fout:', err); }
  };

  const saveAgentConfig = async () => {
    try {
      const config = agentConfig[selectedLeerjaar];
      const rollen = rollenVerdeling[selectedLeerjaar];

      // Converteer rollenVerdeling naar agents array formaat
      const agents = [];
      if (rollen.expert_pro > 0) agents.push({ agent_rol: 'expert_pro', aantal: rollen.expert_pro });
      if (rollen.expert_contra > 0) agents.push({ agent_rol: 'expert_contra', aantal: rollen.expert_contra });
      if (rollen.kennis_pro > 0) agents.push({ agent_rol: 'kennis_pro', aantal: rollen.kennis_pro });
      if (rollen.kennis_contra > 0) agents.push({ agent_rol: 'kennis_contra', aantal: rollen.kennis_contra });

      // Sla eerst de geselecteerde persona's op voor dit leerjaar
      await verzoek('/admin/statements/' + selectedStelling.id + '/personas', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          persona_ids: stellingPersonas[selectedLeerjaar] || [],
          leerjaar: selectedLeerjaar
        })
      });

      // Sla daarna de agent configuratie op
      const response = await verzoek('/admin/statements/' + selectedStelling.id + '/agent-config', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          leerjaar: selectedLeerjaar,
          agents: agents,
          kennis_verdeling: config.kennis_verdeling,
          tijdsduur_chat: config.tijdsduur_chat,
          tijdsduur_evaluatie: config.tijdsduur_evaluatie
        })
      });
      const data = await response.json();
      if (data.success) alert('Configuratie opgeslagen!');
    } catch (err) { console.error('Fout:', err); }
  };

  const togglePersonaSelection = (personaId) => {
    const currentPersonas = stellingPersonas[selectedLeerjaar] || [];
    if (currentPersonas.includes(personaId)) {
      setStellingPersonas({
        ...stellingPersonas,
        [selectedLeerjaar]: currentPersonas.filter(id => id !== personaId)
      });
    } else {
      setStellingPersonas({
        ...stellingPersonas,
        [selectedLeerjaar]: [...currentPersonas, personaId]
      });
    }
  };

  const fetchPersonas = async () => {
    setLoading(true);
    try {
      const response = await verzoek('/admin/personas');
      const data = await response.json();
      if (data.success) setPersonas(data.data.standaard || []);
    } catch (err) { console.error('Fout:', err); }
    finally { setLoading(false); }
  };

  const viewPersona = async (personaId) => {
    try {
      const response = await verzoek('/admin/personas/' + personaId);
      const data = await response.json();
      if (data.success) setSelectedPersona(data.data);
    } catch (err) { console.error('Fout:', err); }
  };

  const fetchSessies = async () => {
    setLoading(true);
    try {
      const response = await verzoek('/admin/sessions');
      const data = await response.json();
      if (data.success) setSessies(data.data);
    } catch (err) { console.error('Fout:', err); }
    finally { setLoading(false); }
  };

  const createSessie = async (e) => {
    e.preventDefault();
    try {
      const params = new URLSearchParams({ statement_id: newSessie.statement_id, class_id: newSessie.class_id, datum_vanaf: newSessie.datum_vanaf });      
      const response = await verzoek('/admin/sessions?' + params, { method: 'POST' });
      const data = await response.json();
      if (data.success) { setNewSessie({ statement_id: '', class_id: '', datum_vanaf: '' }); setShowSessieForm(false); fetchSessies(); }
    } catch (err) { console.error('Fout:', err); }
  };

  const toggleSessie = async (sessieId, isActief) => {
    try {
      const endpoint = isActief ? 'deactivate' : 'activate';
      const response = await verzoek('/admin/sessions/' + sessieId + '/' + endpoint, { method: 'PUT' });
      const data = await response.json();
      if (data.success) fetchSessies();
    } catch (err) { console.error('Fout:', err); }
  };

  const deleteSessie = async (sessieId) => {
    if (!window.confirm('Weet je zeker?')) return;
    try {
      const response = await verzoek('/admin/sessions/' + sessieId, { method: 'DELETE' });
      const data = await response.json();
      if (data.success) fetchSessies();
    } catch (err) { console.error('Fout:', err); }
  };

  const fetchResultaten = async (sessieId) => {
    setSelectedSessieId(sessieId);
    try {
      const response = await verzoek('/admin/sessions/' + sessieId + '/results');
      const data = await response.json();
      if (data.success) setResultaten(data.data);
    } catch (err) { console.error('Fout:', err); }
  };

  const specialeRollen = ['discussieleider', 'afleider', 'echoput', 'twijfelaar'];
  
  // Tel geselecteerde persona's per type (voor huidig leerjaar)
  const currentLeerjaarPersonas = stellingPersonas[selectedLeerjaar] || [];

  const selectedSpecialeRollen = currentLeerjaarPersonas.filter(id => {
    const persona = allPersonas.find(p => p.id === id);
    return persona?.vast_type && specialeRollen.includes(persona.vast_type);
  }).length;

  // Totaal ingevulde rollen
  const totaalRollen = (rollenVerdeling[selectedLeerjaar]?.expert_pro || 0) +
                       (rollenVerdeling[selectedLeerjaar]?.expert_contra || 0) +
                       (rollenVerdeling[selectedLeerjaar]?.kennis_pro || 0) +
                       (rollenVerdeling[selectedLeerjaar]?.kennis_contra || 0);

  // Totaal aantal persona's (excl. discussieleider)
  const totaalPersonas = currentLeerjaarPersonas.filter(id => {
    const persona = allPersonas.find(p => p.id === id);
    return persona?.vast_type !== 'discussieleider';
  }).length;
  
  // Check: totaal kennisrollen moet kleiner of gelijk zijn aan beschikbare persona's (excl. speciale rollen)
  // Kennisrollen worden toegekend aan de persona's die de leerling selecteert
  const totaalTeSelecteren = totaalRollen + selectedSpecialeRollen;
  const isVerdelingCorrect = totaalTeSelecteren <= totaalPersonas && totaalTeSelecteren > 0;

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <img src="/socrates_logo.png" alt="Socrates" style={{ height: '125px' }} />
        <div style={styles.userInfo}>
          <span>{user.school_naam ? user.school_naam + ' | ' : ''}{user.naam}</span>   
          <button onClick={onLogout} style={styles.logoutBtn}>Uitloggen</button>
        </div>
      </div>
      <div style={styles.tabs}>
        <button style={activeTab === 'klassen' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('klassen')}><Users size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Klassen</button>
        <button style={activeTab === 'stellingen' ? styles.tabActive : styles.tab} onClick={() => { setActiveTab('stellingen'); setSelectedStelling(null); }}><MessageSquare size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Stellingen</button>
        <button style={activeTab === 'personas' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('personas')}><Users size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Persona's</button>
        <button style={activeTab === 'sessies' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('sessies')}><ClipboardList size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Sessies</button>
        <button style={activeTab === 'resultaten' ? styles.tabActive : styles.tab} onClick={() => setActiveTab('resultaten')}><BarChart3 size={16} style={{ marginRight: '6px', verticalAlign: 'middle' }} />Resultaten</button>
      </div>
      <div style={styles.content}>

        {activeTab === 'klassen' && (
          <div style={styles.splitView}>
            <div style={styles.listPanel}>
              <div style={styles.panelHeader}>
                <h2>Mijn Klassen</h2>
                <button onClick={() => setShowForm(true)} style={styles.addBtn}>+ Nieuwe Klas</button>
              </div>
              {showForm && (
                <form onSubmit={createKlas} style={styles.form}>
                  <input type="text" placeholder="Naam van de klas" value={newKlasNaam} onChange={(e) => setNewKlasNaam(e.target.value)} style={styles.input} required />
                  <select value={newKlasLeerjaar} onChange={(e) => setNewKlasLeerjaar(parseInt(e.target.value))} style={styles.input}>
                    <option value={6}>Leerjaar 6</option>
                    <option value={7}>Leerjaar 7</option>
                    <option value={8}>Leerjaar 8</option>
                  </select>
                  <div style={styles.formButtons}>
                    <button type="submit" style={styles.saveBtn}>Opslaan</button>
                    <button type="button" onClick={() => setShowForm(false)} style={styles.cancelBtn}>Annuleren</button>
                  </div>
                </form>
              )}
              {loading ? <p>Laden...</p> : klassen.length === 0 ? <p style={styles.empty}>Nog geen klassen.</p> : (
                <ul style={styles.list}>
                  {klassen.map((klas) => (
                    <li key={klas.id} style={selectedKlas?.id === klas.id ? styles.listItemActive : styles.listItem} onClick={() => viewKlas(klas.id)}>
                      <span>{klas.naam} <span style={{color: kleur.grijs, fontSize: '12px'}}>(leerjaar {klas.leerjaar || 6})</span></span>
                      <button onClick={(e) => { e.stopPropagation(); deleteKlas(klas.id); }} style={styles.deleteBtn}>🗑️</button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div style={styles.detailPanel}>
              {selectedKlas ? (
                <>
                  <h2>{selectedKlas.naam}</h2>
                  <div style={styles.panelHeader}>
                    <h3 style={styles.subheader}>Leerlingen ({selectedKlas.students?.length || 0})</h3>
                    <button onClick={() => setShowStudentForm(true)} style={styles.addBtn}>+ Leerling</button>
                  </div>
                  {showStudentForm && (
                    <form onSubmit={addStudent} style={styles.form}>
                      <input type="text" placeholder="Naam" value={newStudent.naam} onChange={(e) => setNewStudent({...newStudent, naam: e.target.value})} style={styles.input} required />
                      <input type="text" placeholder="Gebruikersnaam" value={newStudent.username} onChange={(e) => setNewStudent({...newStudent, username: e.target.value})} style={styles.input} required />
                      <input type="text" placeholder="Wachtwoord" value={newStudent.password} onChange={(e) => setNewStudent({...newStudent, password: e.target.value})} style={styles.input} required />
                      <div style={styles.formButtons}>
                        <button type="submit" style={styles.saveBtn}>Toevoegen</button>
                        <button type="button" onClick={() => setShowStudentForm(false)} style={styles.cancelBtn}>Annuleren</button>
                      </div>
                    </form>
                  )}
                  {selectedKlas.students?.length > 0 ? (
                    <ul style={styles.studentList}>
                      {selectedKlas.students.map((student) => (
                        <li key={student.id} style={styles.studentItem}>
                          <div><span>👤 {student.naam}</span><span style={styles.username}>@{student.username}</span></div>
                          <div style={styles.studentActions}>
                            <button onClick={() => deleteStudent(student.id)} style={styles.deleteBtn}>🗑️</button>
                          </div>
                        </li>
                      ))}
                    </ul>
                  ) : <p style={styles.empty}>Nog geen leerlingen.</p>}
                </>
              ) : <p style={styles.empty}>Selecteer een klas</p>}
            </div>
          </div>
        )}

     {activeTab === 'stellingen' && (
          <div style={styles.splitView}>
            <div style={styles.listPanel}>
              <div style={styles.panelHeader}>
                <h2>Beschikbare stellingen</h2>
              </div>
              {loading ? <p>Laden...</p> : stellingen.length === 0 ? <p style={styles.empty}>Nog geen stellingen beschikbaar.</p> : (
                <ul style={styles.list}>
                  {stellingen.map((stelling) => (
                    <li key={stelling.id} style={selectedStelling?.id === stelling.id ? styles.listItemActive : styles.listItem} onClick={() => viewStelling(stelling.id)}>
                      <span>{stelling.titel}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div style={styles.detailPanel}>
              {selectedStelling ? (
                <>
                  <h2>{selectedStelling.titel}</h2>
                  {selectedStelling.beschrijving && <p style={styles.beschrijving}>{selectedStelling.beschrijving}</p>}
                  

<div style={styles.configSection}>
                    <h3 style={styles.subheader}>Chat samenstelling per leerjaar</h3>
                    <div style={{height: '16px'}}></div>
                    <div style={styles.leerjaarTabs}>
                      {[6, 7, 8].map(jaar => (
                        <button 
                          key={jaar} 
                          onClick={() => setSelectedLeerjaar(jaar)} 
                          style={selectedLeerjaar === jaar ? styles.leerjaarTabActive : styles.leerjaarTab}
                        >
                          Leerjaar {jaar}
                        </button>
                      ))}
                    </div>
                    
                    <div style={{height: '24px'}}></div>
                    
                    <h4>Beschikbare persona's</h4>
                    <p style={styles.beschrijving}>Selecteer welke persona's leerlingen kunnen kiezen.</p>
                    <div style={{height: '12px'}}></div>
                    
                    <div style={styles.personaSelectGrid}>
                      {allPersonas.filter(persona => persona.vast_type !== 'discussieleider').map(persona => (
                        <div 
                          key={persona.id} 
                          onClick={() => togglePersonaSelection(persona.id)}
                          style={(stellingPersonas[selectedLeerjaar] || []).includes(persona.id) ? styles.personaSelectItemActive : styles.personaSelectItem}
                        >
                          <div style={styles.personaSelectName}>{persona.naam}</div>
                          <div style={styles.personaSelectType}>{persona.vast_type || 'flexibel'}</div>
                        </div>
                      ))}
                    </div>
                    
                    <div style={{height: '8px'}}></div>
                    <div style={styles.totalPersonas}>
                      {(stellingPersonas[selectedLeerjaar] || []).length} persona's geselecteerd voor leerjaar {selectedLeerjaar}
                    </div>
                    
                    <div style={{height: '24px'}}></div>
                    
                    <h4>Verdeling Kennisrollen</h4>
                    <p style={styles.beschrijving}>Verdeel de kennisrollen over de geselecteerde persona's (excl. discussieleider, twijfelaar, etc.). Indien persona's zonder kennisrol worden geselecteerd, komen deze in de plaats van de kennisrollen. </p>                    
                    <div style={{height: '12px'}}></div>
                    
                    <div style={styles.rollenGrid}>
                      <div style={styles.rolInput}>
                        <label>Expert PRO</label>
                        <input 
                          type="number" 
                          min="0"
                          value={rollenVerdeling[selectedLeerjaar]?.expert_pro || 0}
                          onChange={(e) => setRollenVerdeling({
                            ...rollenVerdeling,
                            [selectedLeerjaar]: {...rollenVerdeling[selectedLeerjaar], expert_pro: parseInt(e.target.value) || 0}
                          })}
                          style={styles.tijdsduurInput}
                        />
                      </div>
                      <div style={styles.rolInput}>
                        <label>Expert CONTRA</label>
                        <input 
                          type="number" 
                          min="0"
                          value={rollenVerdeling[selectedLeerjaar]?.expert_contra || 0}
                          onChange={(e) => setRollenVerdeling({
                            ...rollenVerdeling,
                            [selectedLeerjaar]: {...rollenVerdeling[selectedLeerjaar], expert_contra: parseInt(e.target.value) || 0}
                          })}
                          style={styles.tijdsduurInput}
                        />
                      </div>
                      <div style={styles.rolInput}>
                        <label>Kennis PRO</label>
                        <input 
                          type="number" 
                          min="0"
                          value={rollenVerdeling[selectedLeerjaar]?.kennis_pro || 0}
                          onChange={(e) => setRollenVerdeling({
                            ...rollenVerdeling,
                            [selectedLeerjaar]: {...rollenVerdeling[selectedLeerjaar], kennis_pro: parseInt(e.target.value) || 0}
                          })}
                          style={styles.tijdsduurInput}
                        />
                      </div>
                      <div style={styles.rolInput}>
                        <label>Kennis CONTRA</label>
                        <input 
                          type="number" 
                          min="0"
                          value={rollenVerdeling[selectedLeerjaar]?.kennis_contra || 0}
                          onChange={(e) => setRollenVerdeling({
                            ...rollenVerdeling,
                            [selectedLeerjaar]: {...rollenVerdeling[selectedLeerjaar], kennis_contra: parseInt(e.target.value) || 0}
                          })}
                          style={styles.tijdsduurInput}
                        />
                      </div>
                    </div>
                    <div style={{height: '8px'}}></div>
                    <div style={{
                      ...styles.totalPersonas,
                      background: isVerdelingCorrect ? kleur.accent : kleur.rood,
                      color: isVerdelingCorrect ? 'black' : 'white'
                    }}>
                      Leerling selecteert: {totaalTeSelecteren} persona's (uit {totaalPersonas} beschikbaar)
                    </div>                    
                    <div style={{height: '24px'}}></div>
                    
                    <div style={styles.tijdsduurGrid}>
                      <div>
                        <h4>Tijdsduur chat (minuten)</h4>
                        <div style={{height: '8px'}}></div>
                        <input 
                          type="number" 
                          value={agentConfig[selectedLeerjaar]?.tijdsduur_chat || 15}
                          onChange={(e) => setAgentConfig({
                            ...agentConfig,
                            [selectedLeerjaar]: {...agentConfig[selectedLeerjaar], tijdsduur_chat: parseInt(e.target.value)}
                          })}
                          style={styles.tijdsduurInput}
                        />
                      </div>
                      <div>
                        <h4>Tijdsduur evaluatie (minuten)</h4>
                        <div style={{height: '8px'}}></div>
                        <input 
                          type="number" 
                          value={agentConfig[selectedLeerjaar]?.tijdsduur_evaluatie || 5}
                          onChange={(e) => setAgentConfig({
                            ...agentConfig,
                            [selectedLeerjaar]: {...agentConfig[selectedLeerjaar], tijdsduur_evaluatie: parseInt(e.target.value)}
                          })}
                          style={styles.tijdsduurInput}
                        />
                      </div>
                    </div>
                    <div style={{height: '16px'}}></div>
                    <button 
                    onClick={saveAgentConfig} 
                      style={{
                        ...styles.saveBtn,
                        opacity: !isVerdelingCorrect ? 0.5 : 1,
                        cursor: !isVerdelingCorrect ? 'not-allowed' : 'pointer'
                      }}
                      disabled={!isVerdelingCorrect}
                    >
                      Configuratie opslaan
                    </button>
		    </div>
                </>
              ) : <p style={styles.empty}>Selecteer een stelling</p>}
            </div>
          </div>
        )}


        {activeTab === 'personas' && (
          <div style={styles.splitView}>
            <div style={styles.listPanel}>
              <div style={styles.panelHeader}><h2>Persona's</h2></div>
              {loading ? <p>Laden...</p> : personas.length === 0 ? <p style={styles.empty}>Geen persona's.</p> : (
                <ul style={styles.list}>
                  {personas.map((persona) => (
                    <li key={persona.id} style={selectedPersona?.id === persona.id ? styles.listItemActive : styles.listItem} onClick={() => viewPersona(persona.id)}>
                      <span>👤 {persona.naam}</span>
                      {persona.vast_type && <span style={styles.vastType}>{persona.vast_type}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </div>
            <div style={styles.detailPanel}>
              {selectedPersona ? (
                <>
                  <h2>
                    <Portret apiUrl={apiUrl} naam={selectedPersona.portret} grootte={44} stijl={styles.portretKop}
                      terugval={<span style={{marginRight: '6px'}}>👤</span>} />
                    {selectedPersona.naam}
                  </h2>
                  <p style={styles.korteOmschrijving}>{selectedPersona.korte_omschrijving}</p>
                  <div style={styles.personaGrid}>
                    <div style={styles.personaInfo}><strong>Geslacht:</strong> {selectedPersona.geslacht || '-'}</div>
                    <div style={styles.personaInfo}><strong>Leeftijd:</strong> {selectedPersona.leeftijd || '-'}</div>
                    <div style={styles.personaInfo}><strong>Woonplaats:</strong> {selectedPersona.woonplaats || '-'}</div>
                    <div style={styles.personaInfo}><strong>Werk:</strong> {selectedPersona.werk || '-'}</div>
                  </div>
                  <h3 style={styles.sectionHeader}>Achtergrond</h3>
                  <p style={styles.personaText}>{selectedPersona.achtergrond}</p>
                  <h3 style={styles.sectionHeader}>Persoonlijkheid</h3>
                  <p style={styles.personaText}>{selectedPersona.persoonlijkheid}</p>
                  <h3 style={styles.sectionHeader}>Spreekstijl</h3>
                  <p style={styles.personaText}>{selectedPersona.spreekstijl}</p>
                </>
              ) : <p style={styles.empty}>Selecteer een persona</p>}
            </div>
          </div>
        )}

        {activeTab === 'sessies' && (
          <div style={styles.card}>
            <div style={styles.panelHeader}>
              <h2>Sessies</h2>
              <button onClick={() => setShowSessieForm(true)} style={styles.addBtn}>+ Nieuwe Sessie</button>
            </div>
            {showSessieForm && (
              <form onSubmit={createSessie} style={styles.form}>
                <select value={newSessie.statement_id} onChange={(e) => setNewSessie({...newSessie, statement_id: e.target.value})} style={styles.input} required>
                  <option value="">-- Kies een stelling --</option>
                  {stellingen.map(s => <option key={s.id} value={s.id}>{s.icon} {s.titel}</option>)}
                </select>
                <select value={newSessie.class_id} onChange={(e) => setNewSessie({...newSessie, class_id: e.target.value})} style={styles.input} required>
                  <option value="">-- Kies een klas --</option>
                  {klassen.map(k => <option key={k.id} value={k.id}>{k.naam}</option>)}
                </select>
                <input type="date" value={newSessie.datum_vanaf} onChange={(e) => setNewSessie({...newSessie, datum_vanaf: e.target.value})} style={styles.input} required />
                <div style={styles.formButtons}>
                  <button type="submit" style={styles.saveBtn}>Aanmaken</button>
                  <button type="button" onClick={() => setShowSessieForm(false)} style={styles.cancelBtn}>Annuleren</button>
                </div>
              </form>
            )}
            {loading ? <p>Laden...</p> : sessies.length === 0 ? <p style={styles.empty}>Nog geen sessies.</p> : (
              <div style={styles.sessiesList}>
                {sessies.map((sessie) => {
                  const stelling = stellingen.find(s => s.id === sessie.statement_id);
                  const klas = klassen.find(k => k.id === sessie.class_id);
                  return (
                    <div key={sessie.id} style={styles.sessieCard}>
                      <div style={styles.sessieInfo}>
                        <div style={styles.sessieTitle}>{stelling?.icon || '📜'} {stelling?.titel || 'Onbekend'}</div>
                        <div style={styles.sessieMeta}>{klas?.naam || 'Onbekend'} (leerjaar {klas?.leerjaar || 6}) • {sessie.datum_vanaf}</div>
                      </div>
                      <div style={styles.sessieActions}>
                        <button onClick={() => toggleSessie(sessie.id, sessie.is_actief)} style={sessie.is_actief ? styles.activeBtn : styles.inactiveBtn}>
                          {sessie.is_actief ? 'Actief' : 'Inactief'}
                        </button>
                        <button onClick={() => deleteSessie(sessie.id)} style={styles.deleteBtn}>🗑️</button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {activeTab === 'resultaten' && (
          <div style={styles.splitView}>
            <div style={styles.listPanel}>
              <div style={styles.panelHeader}>
                <h2>Resultaten per sessie</h2>
              </div>
              {sessies.length === 0 ? (
                <p style={styles.empty}>Nog geen sessies.</p>
              ) : (
                <ul style={styles.list}>
                  {sessies.map((sessie) => {
                    const stelling = stellingen.find(s => s.id === sessie.statement_id);
                    const klas = klassen.find(k => k.id === sessie.class_id);
                    return (
                      <li key={sessie.id} style={selectedSessieId === sessie.id ? styles.listItemActive : styles.listItem} onClick={() => fetchResultaten(sessie.id)}>
                        <div>
                          <div>{stelling?.icon || '📜'} {stelling?.titel || 'Onbekend'}</div>
                          <div style={styles.sessieMeta}>{klas?.naam || 'Onbekend'}</div>
                        </div>
                        {sessie.is_actief && <span style={styles.activeBadge}>Actief</span>}
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
            <div style={styles.detailPanel}>
              {selectedSessieId ? (
                <>
                  <h2>Inzendingen ({resultaten.length})</h2>
                  {resultaten.length === 0 ? (
                    <p style={styles.empty}>Nog geen groepjes hebben de opdracht afgerond.</p>
                  ) : (
                    <div style={styles.resultatenList}>
                      {resultaten.map((r) => (
                        <div key={r.discussion_id} style={styles.resultCard}>
                          <div style={styles.resultHeader}>
                            <span style={styles.resultAvatar}>{r.groepje_avatar}</span>
                            <div>
                              <div style={styles.resultNaam}>{r.groepje_naam}</div>
                              <div style={styles.resultMeta}>{r.students.join(', ')} • {r.chat_count} berichten</div>
                            </div>
                            <span style={r.status === 'klaar' ? styles.statusKlaar : styles.statusBezig}>
                              {r.status === 'klaar' ? '✅ Klaar' : '⏳ Bezig'}
                            </span>
                          </div>
                          {r.rollen?.length > 0 && (
                            <div style={styles.rollenBox}>
                              <div style={styles.rollenKop}>
                                Rolverdeling
                                <span style={styles.rollenBron}>
                                  {r.rolverdeling_bron === 'llm'
                                    ? `door ${r.rolverdeling_model || 'het model'}`
                                    : `op volgorde${r.rolverdeling_reden ? ' — ' + r.rolverdeling_reden : ''}`}
                                </span>
                              </div>
                              <ul style={styles.rollenLijst}>
                                {r.rollen.map((rol, i) => (
                                  <li key={i} style={styles.rolRegel}>
                                    <strong>{rol.naam}</strong>
                                    <span style={styles.rolNaam}>{rol.rol}</span>
                                    {rol.vast && <span style={styles.rolVast}>vaste rol</span>}
                                    {rol.waarom && <span style={styles.rolWaarom}>{rol.waarom}</span>}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {r.gesprek?.length > 0 && (
                            <div style={styles.rollenBox}>
                              <div style={styles.rollenKop}>
                                Het gesprek
                                <span style={styles.rollenBron}>{r.chat_count} berichten</span>
                                <button
                                  onClick={() => setOpenGesprek(openGesprek === r.discussion_id ? null : r.discussion_id)}
                                  style={styles.gesprekKnop}
                                >
                                  {openGesprek === r.discussion_id ? 'verberg' : 'lees mee'}
                                </button>
                              </div>
                              {openGesprek === r.discussion_id && (
                                <div style={styles.gesprekLog}>
                                  {r.gesprek.map((m, i) => (
                                    <div key={i} style={m.type === 'taalcorrectie' ? styles.gesprekCorrectie : styles.gesprekRegel}>
                                      <span style={styles.gesprekVan}>{m.van}</span>
                                      <span>{m.bericht}</span>
                                      {m.geblokkeerd && (
                                        <div style={styles.gesprekGeblokkeerd}>
                                          tegengehouden bericht van de leerling: “{m.geblokkeerd}”
                                          {m.termen?.length > 0 && ' — ' + m.termen.join(', ')}
                                        </div>
                                      )}
                                    </div>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                          {r.socrates_hulp?.length > 0 && (
                            <div style={styles.rollenBox}>
                              <div style={styles.rollenKop}>
                                Hulp van Socrates
                                <span style={styles.rollenBron}>{r.socrates_hulp.length}x ingeschakeld</span>
                              </div>
                              <ul style={styles.rollenLijst}>
                                {r.socrates_hulp.map((h, i) => (
                                  <li key={i} style={styles.rolRegel}>
                                    <span style={styles.rolNaam}>{h.soort}</span>
                                    {h.tekst && <span style={styles.hulpOverTekst}>over: “{h.tekst}”</span>}
                                    <span style={styles.rolWaarom}>{h.fout ? 'niet gelukt: ' + h.fout : h.antwoord}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          {r.status === 'klaar' && (
                            <div style={styles.argumentenBox}>
                              <div style={styles.argumentenVoor}>
                                <strong>✅ VOOR:</strong>
                                <ul>{r.argumenten_voor.map((a, i) => <li key={i}>{a}</li>)}</ul>
                              </div>
                              <div style={styles.argumentenTegen}>
                                <strong>❌ TEGEN:</strong>
                                <ul>{r.argumenten_tegen.map((a, i) => <li key={i}>{a}</li>)}</ul>
                              </div>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <p style={styles.empty}>Selecteer een sessie om resultaten te zien</p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

const styles = {
  ...gedeeld,
  header: { background: 'white', color: kleur.inkt, padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  userInfo: { display: 'flex', alignItems: 'center', gap: '16px' },
  tabs: { display: 'flex', background: 'white', borderBottom: '1px solid black', padding: '0 24px' },
  content: { padding: '24px' },
  addBtn: { padding: '8px 16px', background: kleur.accent, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  input: { width: '100%', padding: '12px', border: `2px solid ${kleur.lijn}`, borderRadius: '6px', fontSize: '14px', marginBottom: '12px', boxSizing: 'border-box' },
  formButtons: { display: 'flex', gap: '8px' },
  saveBtn: { padding: '8px 16px', background: kleur.accent, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer' },
  listItemActive: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', borderRadius: '8px', marginBottom: '8px', cursor: 'pointer', background: kleur.accentLicht, border: `2px solid ${kleur.accent}` },
  deleteBtn: { background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px' },
  studentList: { listStyle: 'none', padding: 0 },
  studentItem: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '12px 16px', background: kleur.vlak, borderRadius: '6px', marginBottom: '8px' },
  username: { color: kleur.stiller, fontSize: '13px', marginLeft: '8px' },
  studentActions: { display: 'flex', alignItems: 'center', gap: '8px' },
  card: { background: 'white', padding: '24px', borderRadius: '12px', border: '1px solid black' },
  beschrijving: { color: kleur.stil, marginBottom: '24px' },
  vastType: { background: kleur.accent, color: 'white', padding: '2px 8px', borderRadius: '4px', fontSize: '11px' }, 
  rollenBox: { background: kleur.vlakKoel, borderRadius: '8px', padding: '12px 14px', margin: '10px 0' },
  rollenKop: { fontSize: '13px', fontWeight: 'bold', marginBottom: '8px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'baseline' },
  rollenBron: { fontWeight: 'normal', color: kleur.tekstStil, fontSize: '12px' },
  rollenLijst: { listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: '6px' },
  rolRegel: { fontSize: '13px', display: 'flex', flexWrap: 'wrap', gap: '8px', alignItems: 'baseline' },
  rolNaam: { background: kleur.accentLicht, color: kleur.accentTekst, padding: '2px 8px', borderRadius: '10px', fontSize: '12px' },
  rolVast: { color: kleur.grijs, fontSize: '12px' },
  rolWaarom: { color: kleur.tekstZacht, fontStyle: 'italic', flexBasis: '100%' },
  hulpOverTekst: { color: kleur.tekstStil, fontSize: '12px' },
  gesprekKnop: { marginLeft: 'auto', border: `1px solid ${kleur.accent}`, background: 'white', color: kleur.accentTekst, borderRadius: '8px', padding: '2px 10px', fontSize: '12px', cursor: 'pointer', font: 'inherit' },
  gesprekLog: { maxHeight: '320px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' },
  gesprekRegel: { fontSize: '13px', lineHeight: 1.5 },
  gesprekCorrectie: { fontSize: '13px', lineHeight: 1.5, background: kleur.amber, border: `1px solid ${kleur.amberLijn}`, borderRadius: '8px', padding: '8px 10px' },
  gesprekGeblokkeerd: { color: kleur.amberTekst, fontSize: '12px', marginTop: '4px' },
  portretKop: { verticalAlign: 'middle', marginRight: '10px' },
  korteOmschrijving: { color: kleur.stil, fontSize: '16px', marginBottom: '24px' },
  personaGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '24px', padding: '16px', background: kleur.vlak, borderRadius: '8px' },
  personaInfo: { fontSize: '14px' },
  sectionHeader: { fontSize: '14px', color: kleur.inkt, marginTop: '20px', marginBottom: '8px', paddingBottom: '4px' },
  personaText: { fontSize: '14px', lineHeight: '1.6', color: kleur.tekstZacht },
  sessiesList: { display: 'flex', flexDirection: 'column', gap: '12px' },
  sessieCard: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px', background: kleur.vlak, borderRadius: '8px' },
  sessieInfo: { flex: 1 },
  sessieTitle: { fontSize: '16px', fontWeight: 'bold', marginBottom: '4px' },
  sessieMeta: { fontSize: '13px', color: kleur.stil },
  sessieActions: { display: 'flex', alignItems: 'center', gap: '8px' },
  activeBtn: { padding: '8px 16px', background: kleur.accent, color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', width: '80px', textAlign: 'center' },
  inactiveBtn: { padding: '8px 16px', background: 'white', color: 'black', border: '1px solid black', borderRadius: '6px', cursor: 'pointer', width: '80px', textAlign: 'center' },
  activeBadge: { background: kleur.accent, color: 'white', padding: '4px 8px', borderRadius: '4px', fontSize: '11px' },
  resultatenList: { display: 'flex', flexDirection: 'column', gap: '16px' },
  resultCard: { background: kleur.vlak, borderRadius: '12px', padding: '16px' },
  resultHeader: { display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '12px' },
  resultAvatar: { fontSize: '32px' },
  resultNaam: { fontWeight: 'bold', fontSize: '16px' },
  resultMeta: { fontSize: '12px', color: kleur.stil },
  statusKlaar: { background: kleur.accent, color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', marginLeft: 'auto' },
  statusBezig: { background: kleur.oranje, color: 'white', padding: '4px 12px', borderRadius: '12px', fontSize: '12px', marginLeft: 'auto' },
  argumentenBox: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginTop: '12px' },
  argumentenVoor: { background: kleur.accentLichter, padding: '12px', borderRadius: '8px', fontSize: '14px' },
  argumentenTegen: { background: kleur.roodLicht, padding: '12px', borderRadius: '8px', fontSize: '14px' },
  configSection: { marginTop: '24px', paddingTop: '24px', borderTop: `1px solid ${kleur.lijn}` },
  leerjaarTabs: { display: 'flex', gap: '8px', marginBottom: '16px' },
  leerjaarTab: { padding: '10px 20px', border: `1px solid ${kleur.lijn}`, background: 'white', borderRadius: '6px', cursor: 'pointer', fontSize: '14px' },
  leerjaarTabActive: { padding: '10px 20px', border: '2px solid black', background: 'white', borderRadius: '6px', cursor: 'pointer', fontSize: '14px', fontWeight: 'bold' },
  tijdsduurGrid: { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' },
  tijdsduurInput: { width: '100px', padding: '10px', border: '1px solid black', borderRadius: '6px', fontSize: '16px' },
  personaSelectGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '12px' },
  personaSelectItem: { padding: '12px', background: kleur.vlak, borderRadius: '8px', cursor: 'pointer', border: `1px solid ${kleur.lijn}` },
  personaSelectItemActive: { padding: '12px', background: kleur.accentLichter, borderRadius: '8px', cursor: 'pointer', border: '2px solid black' },
  personaSelectName: { fontWeight: 'bold', marginBottom: '4px' },
  personaSelectType: { fontSize: '12px', color: kleur.stil },
  totalPersonas: { padding: '12px', background: kleur.vlak, borderRadius: '6px', fontWeight: 'bold', textAlign: 'center' },
  rollenGrid: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px' },
  rolInput: { display: 'flex', flexDirection: 'column', gap: '4px' },
};

export default AdminDashboard;


